import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { id as idLoc, ms as msLoc, fr as frLoc, de as deLoc, enGB } from "date-fns/locale";
const LOCALES = { id: idLoc, ms: msLoc, fr: frLoc, de: deLoc, en: enGB };
import { Users, CalendarDays, MapPin, Check, Loader2, Flame, ShieldCheck, Car, Clock, Sparkles, BadgeCheck, Share2, MessageCircle, Instagram } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useLang } from "@/context/LangContext";
import { useApi, http, formatErr } from "@/lib/api";
import { COUNTRIES } from "@/pages/ReviewInvite";

const LANG_COUNTRY = { id: "ID", ms: "MY", en: "GB", fr: "FR", de: "DE" };
const ISO = (d) => format(d, "yyyy-MM-dd");
const input = "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#064E3B]";
const myTrips = () => JSON.parse(localStorage.getItem("ptg_my_trips") || "[]");
const rememberTrip = (id) => localStorage.setItem("ptg_my_trips", JSON.stringify([...new Set([...myTrips(), id])]));

function PersonFields({ form, setForm }) {
  const { t } = useLang();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <input data-testid="ot-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder={t("full_name")} className={input} />
      <input data-testid="ot-phone-input" required minLength={6} value={form.phone} onChange={set("phone")} placeholder={`${t("phone")} (+60 / 08…)`} className={input} />
      <select data-testid="ot-country-select" value={form.country} onChange={set("country")} className={input}>
        {Object.entries(COUNTRIES).map(([c, n]) => <option key={c} value={c}>{n}</option>)}
      </select>
      <select data-testid="ot-pax-select" value={form.pax} onChange={set("pax")} className={input}>
        {[1, 2, 3].map((n) => <option key={n} value={n}>{n} {t("ot_person")}</option>)}
      </select>
    </div>
  );
}

const Agree = ({ checked, onChange }) => {
  const { t } = useLang();
  return (
    <label className="flex items-start gap-2 text-xs text-slate-600">
      <input type="checkbox" data-testid="ot-terms-checkbox" required checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#064E3B]" />
      <span>{t("ot_agree")} <a href="#ot-terms" className="font-bold text-[#064E3B] underline">{t("ot_terms_title")}</a></span>
    </label>
  );
};

function useBlankPerson() {
  const { lang } = useLang();
  return { name: "", phone: "", pax: 1, country: LANG_COUNTRY[lang] || "ID" };
}

function RequestForm({ pois, onCreated }) {
  const { t, lang } = useLang();
  const [picked, setPicked] = useState([]);
  const [range, setRange] = useState();
  const [form, setForm] = useState(useBlankPerson());
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const minDate = new Date(Date.now() + 5 * 86400000);
  const toggle = (s) => setPicked(picked.includes(s) ? picked.filter((x) => x !== s) : [...picked, s]);
  const submit = async (e) => {
    e.preventDefault();
    if (!picked.length) return toast.error(t("ot_pick_one"));
    if (!range?.from || !range?.to) return toast.error(t("ot_pick_dates"));
    setBusy(true);
    try {
      const { data } = await http.post("/trip-requests", { ...form, pax: Number(form.pax), lang, destinations: picked, date_from: ISO(range.from), date_to: ISO(range.to) });
      rememberTrip(data.id);
      toast.success(t("ot_created"));
      setPicked([]); setRange(undefined); setAgree(false);
      onCreated(data);
    } catch (err) { toast.error(formatErr(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="ot-request-form" className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-xl shadow-slate-200/50">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E11D48]">1 · {t("ot_step_pick")}</p>
      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
        {pois.map((p) => {
          const on = picked.includes(p.slug);
          return (
            <button type="button" key={p.slug} data-testid={`ot-poi-${p.slug}`} onClick={() => toggle(p.slug)}
              className={`flex items-start gap-2 rounded-2xl border p-3 text-left text-sm transition-colors ${on ? "border-[#064E3B] bg-emerald-50" : "border-slate-200 hover:border-slate-400"}`}>
              <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${on ? "border-[#064E3B] bg-[#064E3B] text-white" : "border-slate-300"}`}>{on && <Check className="h-3.5 w-3.5" />}</span>
              <span><b className="block leading-tight">{p.name}</b><span className="text-xs text-slate-500">{p.area}</span></span>
            </button>
          );
        })}
      </div>
      <p className="mt-8 text-xs font-bold uppercase tracking-[0.2em] text-[#E11D48]">2 · {t("ot_step_dates")}</p>
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" data-testid="ot-date-range-btn" className={`${input} mt-3 flex items-center gap-2 text-left`}>
            <CalendarDays className="h-4 w-4 text-[#064E3B]" />
            {range?.from ? `${format(range.from, "dd MMM yyyy", { locale: LOCALES[lang] })} – ${range.to ? format(range.to, "dd MMM yyyy", { locale: LOCALES[lang] }) : "…"}` : t("ot_pick_dates")}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="range" locale={LOCALES[lang]} selected={range} onSelect={setRange} disabled={range?.from && !range?.to ? [{ before: minDate }, { after: new Date(range.from.getTime() + 14 * 86400000) }] : { before: minDate }} numberOfMonths={1} data-testid="ot-calendar" />
        </PopoverContent>
      </Popover>
      <p className="mt-8 text-xs font-bold uppercase tracking-[0.2em] text-[#E11D48]">3 · {t("ot_step_you")}</p>
      <div className="mt-3 space-y-3">
        <PersonFields form={form} setForm={setForm} />
        <Agree checked={agree} onChange={setAgree} />
      </div>
      <button disabled={busy} data-testid="ot-submit-btn" className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#E11D48] py-4 font-bold text-white transition-colors hover:bg-[#BE123C] disabled:opacity-60">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}{t("ot_submit")}
      </button>
    </form>
  );
}

function ShareTrip({ trip, names, need }) {
  const { t, lang } = useLang();
  const url = `${window.location.origin}/open-trip#trip-${trip.code}`;
  const text = t("ot_share_text").replace("{route}", trip.destinations.map((d) => names[d] || d).join(" → "))
    .replace("{dates}", `${format(parseISO(trip.date_from), "dd MMM", { locale: LOCALES[lang] })} – ${format(parseISO(trip.date_to), "dd MMM", { locale: LOCALES[lang] })}`)
    .replace("{n}", need) + " " + url;
  const native = async () => { try { await navigator.share({ text }); } catch { /* cancelled */ } };
  const insta = async () => { await navigator.clipboard?.writeText(text); toast.success(t("ot_copied_ig")); window.open("https://www.instagram.com/", "_blank", "noopener"); };
  const btn = "flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition-colors hover:bg-slate-100";
  return (
    <div className="mt-4 flex items-center gap-2">
      <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("ot_invite")}</span>
      <a href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer" data-testid={`ot-share-wa-${trip.code}`} title="WhatsApp" className={`${btn} text-[#25D366]`}><MessageCircle className="h-4 w-4" /></a>
      <button type="button" onClick={insta} data-testid={`ot-share-ig-${trip.code}`} title="Instagram" className={`${btn} text-[#E1306C]`}><Instagram className="h-4 w-4" /></button>
      {typeof navigator !== "undefined" && navigator.share && <button type="button" onClick={native} data-testid={`ot-share-native-${trip.code}`} className={btn}><Share2 className="h-4 w-4" /></button>}
    </div>
  );
}

function TripCard({ trip, names, onJoin }) {
  const { t, money, lang } = useLang();
  const mine = myTrips().includes(trip.id);
  const need = Math.max(0, trip.min_pax - trip.total_pax);
  const pct = Math.min(100, (trip.total_pax / trip.min_pax) * 100);
  const full = trip.total_pax >= trip.max_pax || trip.status === "approved";
  const statusText = trip.status === "approved" ? t("ot_status_approved") : need > 0 ? t("ot_need").replace("{n}", need) : t("ot_status_ready");
  return (
    <div id={`trip-${trip.code}`} data-testid={`ot-trip-${trip.code}`} className="flex scroll-mt-24 flex-col rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-xl target:ring-4 target:ring-[#FBBF24]">
      <div className="flex items-center justify-between gap-2">
        <span className={`rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-wide ${trip.status === "approved" ? "bg-emerald-100 text-emerald-800" : trip.status === "ready" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}`}>{t(`ot_badge_${trip.status}`)}</span>
        <span className="font-mono text-xs text-slate-400">{trip.code}</span>
      </div>
      <p className="mt-4 text-lg font-bold leading-snug">{trip.destinations.map((d) => names[d] || d).join(" → ")}</p>
      <p className="mt-2 flex items-center gap-1.5 text-sm text-slate-500"><CalendarDays className="h-4 w-4" />{format(parseISO(trip.date_from), "dd MMM", { locale: LOCALES[lang] })} – {format(parseISO(trip.date_to), "dd MMM yyyy", { locale: LOCALES[lang] })}</p>
      {trip.meeting_point && <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500"><MapPin className="h-4 w-4" />{trip.meeting_point}</p>}
      <div className="mt-5">
        <div className="h-2.5 overflow-hidden rounded-full bg-slate-100"><div data-testid={`ot-progress-${trip.code}`} className="h-full rounded-full bg-gradient-to-r from-[#E11D48] to-[#FBBF24] transition-all" style={{ width: `${pct}%` }} /></div>
        <p data-testid={`ot-status-${trip.code}`} className="mt-2 text-sm font-semibold text-slate-800">{statusText}</p>
        <p className="text-xs text-slate-500">{trip.total_pax}/{trip.max_pax} {t("ot_seats")}</p>
      </div>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {trip.travellers.map((p, i) => <span key={i} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">{p.name}{p.country ? ` · ${p.country}` : ""}{p.pax > 1 ? ` +${p.pax - 1}` : ""}</span>)}
      </div>
      {trip.status !== "approved" && <ShareTrip trip={trip} names={names} need={need} />}
      <div className="mt-auto pt-6 flex items-end justify-between gap-3">
        <p className="text-sm">{trip.final_price ? <><b className="text-lg">{money(trip.final_price)}</b><span className="text-slate-500">{t("per_person")}</span></> : <span className="text-slate-500">{t("ot_price_tbd")}</span>}</p>
        {mine ? <span data-testid={`ot-joined-${trip.code}`} className="inline-flex items-center gap-1 text-sm font-bold text-emerald-700"><BadgeCheck className="h-4 w-4" />{t("ot_you_joined")}</span>
          : <button data-testid={`ot-join-btn-${trip.code}`} disabled={full} onClick={() => onJoin(trip)} className="shrink-0 whitespace-nowrap rounded-full bg-slate-900 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-slate-700 disabled:opacity-40">{trip.status === "approved" ? t("ot_badge_approved") : full ? t("ot_full") : t("ot_join")}</button>}
      </div>
    </div>
  );
}

function JoinDialog({ trip, names, onClose, onJoined }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState(useBlankPerson());
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post(`/trip-requests/${trip.id}/join`, { ...form, pax: Number(form.pax), lang });
      rememberTrip(trip.id);
      toast.success(t("ot_joined_ok"));
      onJoined(data);
    } catch (err) { toast.error(formatErr(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ot-join-dialog" className="sm:max-w-lg rounded-3xl">
        <DialogHeader><DialogTitle>{t("ot_join")} · {trip.destinations.map((d) => names[d] || d).join(" → ")}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <PersonFields form={form} setForm={setForm} />
          <Agree checked={agree} onChange={setAgree} />
          <button disabled={busy} data-testid="ot-join-submit-btn" className="flex w-full items-center justify-center gap-2 rounded-full bg-[#E11D48] py-3.5 font-bold text-white hover:bg-[#BE123C] disabled:opacity-60">
            {busy && <Loader2 className="h-5 w-5 animate-spin" />}{t("ot_join")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Terms() {
  const { t } = useLang();
  const items = [[Clock, "ot_t1", "ot_t1d"], [Car, "ot_t2", "ot_t2d"], [MapPin, "ot_t3", "ot_t3d"], [ShieldCheck, "ot_t4", "ot_t4d"]];
  return (
    <section id="ot-terms" data-testid="ot-terms" className="mx-auto mt-20 max-w-7xl px-4 sm:px-6 lg:px-8">
      <h2 className="text-2xl font-bold">{t("ot_terms_title")}</h2>
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {items.map(([Icon, k, d]) => (
          <div key={k} className="flex gap-4 rounded-3xl border border-slate-200 bg-white p-5">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-[#064E3B]"><Icon className="h-5 w-5" /></span>
            <div><p className="font-bold">{t(k)}</p><p className="mt-1 text-sm text-slate-600">{t(d)}</p></div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function OpenTrip() {
  const { t } = useLang();
  const poisData = useApi("/pois").data;
  const pois = useMemo(() => poisData || [], [poisData]);
  const { data: trips, setData } = useApi("/trip-requests");
  const stats = useApi("/trip-requests/stats").data || [];
  const [joining, setJoining] = useState(null);
  const names = useMemo(() => Object.fromEntries(pois.map((p) => [p.slug, p.name])), [pois]);
  useEffect(() => {
    if (!trips?.length || !window.location.hash.startsWith("#trip-")) return;
    const el = document.getElementById(window.location.hash.slice(1));
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("ring-4", "ring-[#FBBF24]");
  }, [trips]);
  const upsert = (trip) => setData([trip, ...(trips || []).filter((x) => x.id !== trip.id)]);
  return (
    <div data-testid="open-trip-page" className="pb-24">
      <section className="mx-auto max-w-7xl px-4 pt-12 sm:px-6 lg:px-8">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#E11D48]">Open Trip · {t("ot_kicker")}</p>
        <h1 className="mt-3 max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">{t("ot_title")}</h1>
        <p className="mt-4 max-w-2xl text-base md:text-lg text-slate-500">{t("ot_sub")}</p>
        {stats.length > 0 && (
          <div data-testid="ot-stats" className="no-scrollbar mt-8 flex gap-3 overflow-x-auto">
            {stats.slice(0, 6).map((s) => (
              <span key={s.slug} className="flex shrink-0 items-center gap-2 rounded-full bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-900 ring-1 ring-amber-200">
                <Flame className="h-4 w-4 text-[#E11D48]" />{t("ot_stat").replace("{n}", s.travellers).replace("{place}", names[s.slug] || s.slug)}
              </span>
            ))}
          </div>
        )}
      </section>
      <section className="mx-auto mt-12 max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2 className="flex items-center gap-2 text-2xl font-bold"><Users className="h-6 w-6 text-[#064E3B]" />{t("ot_routes")}</h2>
        {trips?.length ? (
          <div className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">{trips.map((tr) => <TripCard key={tr.id} trip={tr} names={names} onJoin={setJoining} />)}</div>
        ) : <p data-testid="ot-empty" className="mt-6 rounded-3xl border border-dashed border-slate-300 p-10 text-center text-slate-500">{t("ot_empty")}</p>}
      </section>
      <section className="mx-auto mt-16 max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold">{t("ot_propose")}</h2>
        <p className="mt-1 mb-6 text-slate-500">{t("ot_propose_sub")}</p>
        <RequestForm pois={pois} onCreated={upsert} />
      </section>
      <Terms />
      {joining && <JoinDialog trip={joining} names={names} onClose={() => setJoining(null)} onJoined={(tr) => { upsert(tr); setJoining(null); }} />}
    </div>
  );
}
